import type { IconButtonProps } from '@hover/blueprint';
import {
  Box,
  Grid,
  Heading,
  Icon,
  IconButton,
  cssVar,
  forwardRef,
  useBreakpointValue,
} from '@hover/blueprint';
import { zIndices } from '@hover/blueprint/foundation';
import { iChevronLeft, iconChevronLeft, iconInspiration } from '@hover/icons';
import React from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router';

import { CustomizeMobileHeading } from 'src/modules/homeownerApp/components/Customize/CustomizeMobileHeading';
import { nudge } from 'src/modules/homeownerApp/helpers';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { Route } from 'src/shared/route';

import { useFeaturedDisplayImages } from '../Embed/hooks/useFeaturedDisplayImages';

import { useCustomization } from './useCustomization';

const $actionsHeight = cssVar('customize-actions-height');
const $actionsGap = cssVar('customize-actions-gap');

export interface CustomizeEmbedHeadingProps {}

export const CustomizeEmbedHeading = forwardRef<
  CustomizeEmbedHeadingProps,
  any
>((_, ref) => {
  const { baseImage, category, targetImage, onCancel } = useCustomization();
  const { showLandingPage } = useEmbedContext();
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.SHARED);
  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );
  const isInspire = !!useRouteMatch(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path);
  const { featuredDisplayImages } = useFeaturedDisplayImages(
    targetImage.aiOriginalImageId,
  );
  const hideEmbedHeader =
    !baseImage.fromSample || (isInspire && !isMobile && !showLandingPage);

  if (hideEmbedHeader) return null;

  const goBack = () => {
    if (isInspire) {
      history.push({ pathname: Route.EMBED.path });
    } else {
      onCancel();
      history.push({
        pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
          aiDisplayImageId: featuredDisplayImages?.[0].id || targetImage.id,
        }),
      });
    }
  };

  const BackButton = ({
    ...props
  }: Omit<IconButtonProps, 'icon' | 'label'>) => (
    <IconButton
      colorScheme="light"
      icon={iconChevronLeft}
      iconSize={{ base: 'small', tablet: 'medium' }}
      justifySelf="start"
      label="Back"
      left={{ base: 0, tablet: 500 }}
      onClick={goBack}
      position={{ base: 'relative', tablet: 'absolute' }}
      ref={ref}
      size={{
        base: 'small',
        tablet: 'medium',
      }}
      top={{ base: 0, tablet: 500 }}
      variant={{ base: 'ghost', tablet: 'glass' }}
      zIndex={zIndices.overlay}
      {...props}
    />
  );

  if (isMobile && isInspire) {
    return (
      <Grid alignItems="center" gridTemplateColumns="auto 1fr" w={1}>
        {showLandingPage && (
          <BackButton {...nudge(100, 'left')} gridColumn={1} gridRow={1} />
        )}
        <Box
          alignItems="center"
          flexDirection="row"
          gridColumn="1/3"
          gridRow={1}
          justifyContent="center"
        >
          <Heading
            as="h1"
            color="neutral.0"
            noMargin
            size={300}
            textAlign="center"
            textStyle="body500"
          >
            {t('embed.designIdeas')}
          </Heading>
          <Icon color="white" icon={iconInspiration} pl={100} size="small" />
        </Box>
      </Grid>
    );
  }

  if (isMobile && category) {
    return (
      <CustomizeMobileHeading
        backButtonIcon={iChevronLeft}
        category={category}
        height={$actionsHeight.reference}
        hideApplyCustomization
        key="heading"
        mb={$actionsGap.reference}
        onBack={goBack}
        ref={ref}
        width="100%"
      />
    );
  }

  return <BackButton />;
});
